import React, { useState, useEffect } from 'react';
import { Users, Trash2, Ban, CheckCircle, XCircle, Edit2, Eye, EyeOff, Key } from 'lucide-react';
import toast from 'react-hot-toast';

const Resellers = () => {
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [editDiscount, setEditDiscount] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editBalance, setEditBalance] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchResellers();
  }, []);

  const fetchResellers = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await fetch('/api/resellers', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setResellers(data.data.resellers);
      } else {
        toast.error('Failed to load resellers');
      }
    } catch (error) {
      console.error('Error fetching resellers:', error);
      toast.error('Error loading resellers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (resellerId, resellerEmail) => {
    if (!window.confirm(`Are you sure you want to deactivate ${resellerEmail}? This will invalidate their login.`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/resellers/${resellerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Reseller deactivated successfully');
        fetchResellers();
      } else {
        toast.error(data.message || 'Failed to deactivate reseller');
      }
    } catch (error) {
      console.error('Error deactivating reseller:', error);
      toast.error('Error deactivating reseller');
    }
  };

  const handleReactivate = async (resellerId, resellerEmail) => {
    if (!window.confirm(`Are you sure you want to reactivate ${resellerEmail}?`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/resellers/${resellerId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'approved' })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Reseller reactivated successfully');
        fetchResellers();
      } else {
        toast.error(data.message || 'Failed to reactivate reseller');
      }
    } catch (error) {
      console.error('Error reactivating reseller:', error);
      toast.error('Error reactivating reseller');
    }
  };

  const handlePermanentDelete = async (resellerId, resellerEmail) => {
    if (!window.confirm(`⚠️ PERMANENT DELETE: Are you absolutely sure you want to permanently delete ${resellerEmail}? This action CANNOT be undone!`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/resellers/${resellerId}?permanent=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Reseller permanently deleted');
        fetchResellers();
      } else {
        toast.error(data.message || 'Failed to delete reseller');
      }
    } catch (error) {
      console.error('Error deleting reseller:', error);
      toast.error('Error deleting reseller');
    }
  };

  const handleEditDiscount = (reseller) => {
    setSelectedReseller(reseller);
    setEditDiscount(reseller.discount_percentage || '0');
    setShowEditModal(true);
  };

  const handleSaveDiscount = async () => {
    const discount = parseFloat(editDiscount);
    if (isNaN(discount) || discount < 0 || discount > 100) {
      toast.error('Discount must be between 0 and 100');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/resellers/${selectedReseller.id}/discount`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discount_percentage: discount })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Discount updated successfully');
        setShowEditModal(false);
        fetchResellers();
      } else {
        toast.error(data.message || 'Failed to update discount');
      }
    } catch (error) {
      console.error('Error updating discount:', error);
      toast.error('Error updating discount');
    }
  };

  const handleEditPassword = (reseller) => {
    setSelectedReseller(reseller);
    setEditPassword(reseller.password || '');
    setShowPasswordModal(true);
  };

  const handleSavePassword = async () => {
    if (!editPassword || editPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/resellers/${selectedReseller.id}/password`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: editPassword })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        fetchResellers();
      } else {
        toast.error(data.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Error updating password');
    }
  };

  const handleEditBalance = (reseller) => {
    setSelectedReseller(reseller);
    setEditBalance(reseller.balance || '0');
    setShowBalanceModal(true);
  };

  const handleSaveBalance = async () => {
    const balance = parseFloat(editBalance);
    if (isNaN(balance) || balance < 0) {
      toast.error('Balance must be a positive number');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/resellers/${selectedReseller.id}/balance`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ balance: balance })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Balance updated successfully');
        setShowBalanceModal(false);
        fetchResellers();
      } else {
        toast.error(data.message || 'Failed to update balance');
      }
    } catch (error) {
      console.error('Error updating balance:', error);
      toast.error('Error updating balance');
    }
  };

  const togglePasswordVisibility = (resellerId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [resellerId]: !prev[resellerId]
    }));
  };

  const handleDeactivateApiKey = async (resellerId, resellerEmail) => {
    if (!window.confirm(`⚠️ Are you sure you want to deactivate the API key for ${resellerEmail}? This will invalidate their API links and they will need to generate a new key.`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/resellers/${resellerId}/api-key`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('API key deactivated successfully');
        fetchResellers();
      } else {
        toast.error(data.message || 'Failed to deactivate API key');
      }
    } catch (error) {
      console.error('Error deactivating API key:', error);
      toast.error('Error deactivating API key');
    }
  };

  const getStatusBadge = (status, deactivatedBy) => {
    const styles = {
      approved: { bg: '#22c55e', text: 'Approved' },
      pending: { bg: '#f59e0b', text: 'Pending' },
      rejected: { bg: '#ef4444', text: 'Rejected' },
      deactivated: { bg: '#6b7280', text: deactivatedBy === 'admin' ? 'Deactivated (Admin)' : 'Deactivated (User)' }
    };

    const style = styles[status] || styles.pending;

    return (
      <span style={{
        backgroundColor: style.bg,
        color: '#ffffff',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        whiteSpace: 'nowrap'
      }}>
        {style.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', color: '#ffffff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Users className="h-6 w-6" />
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Reseller Management</h2>
        </div>
        <p style={{ color: '#9ca3af', marginTop: '16px' }}>Loading resellers...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Users className="h-6 w-6" style={{ color: '#3b198e' }} />
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#ffffff' }}>
            Reseller Management
          </h2>
        </div>
        <p style={{ color: '#9ca3af', margin: 0 }}>
          Manage all reseller accounts and permissions
        </p>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '24px' }}>
        <input
          type="text"
          placeholder="Search by email, Discord name, or Discord ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 16px',
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '8px',
            color: '#ffffff',
            fontSize: '14px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = '#3b198e'}
          onBlur={(e) => e.target.style.borderColor = '#4a4a4a'}
        />
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', padding: '16px' }}>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 8px 0' }}>Total Resellers</p>
          <p style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{resellers.length}</p>
        </div>
        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', padding: '16px' }}>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 8px 0' }}>Active</p>
          <p style={{ color: '#22c55e', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            {resellers.filter(r => r.status === 'approved').length}
          </p>
        </div>
        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', padding: '16px' }}>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 8px 0' }}>Deactivated</p>
          <p style={{ color: '#6b7280', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            {resellers.filter(r => r.status === 'deactivated').length}
          </p>
        </div>
      </div>

      {/* Resellers Table */}
      <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0a0a0a', borderBottom: '1px solid #4a4a4a' }}>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Discord</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Email</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Password</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Role</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Balance</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Discount</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>API Key</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Status</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Applied</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Approved</th>
                <th style={{ padding: '10px 8px', textAlign: 'right', color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resellers
                .filter((reseller) => {
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase();
                  return (
                    reseller.email?.toLowerCase().includes(query) ||
                    reseller.discord_username?.toLowerCase().includes(query) ||
                    reseller.discord_id?.toLowerCase().includes(query)
                  );
                })
                .map((reseller) => (
                <tr key={reseller.id} style={{ borderBottom: '1px solid #4a4a4a' }}>
                  <td style={{ padding: '10px 8px', color: '#ffffff', fontSize: '14px' }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{reseller.discord_username}</div>
                      <div style={{ color: '#9ca3af', fontSize: '12px' }}>{reseller.discord_id}</div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 8px', color: '#ffffff', fontSize: '14px' }}>{reseller.email}</td>
                  <td style={{ padding: '10px 8px', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontFamily: 'monospace', color: '#ffffff' }}>
                        {visiblePasswords[reseller.id] ? reseller.password : '••••••••'}
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(reseller.id)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#9ca3af',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title={visiblePasswords[reseller.id] ? 'Hide password' : 'Show password'}
                      >
                        {visiblePasswords[reseller.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleEditPassword(reseller)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#3b198e',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Edit password"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '10px 8px', fontSize: '14px' }}>
                    <span style={{
                      backgroundColor: (reseller.role === 'admin' || reseller.is_admin === 1) ? '#7c3aed' : '#6b7280',
                      color: '#ffffff',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {(reseller.role === 'admin' || reseller.is_admin === 1) ? 'Admin' : 'Reseller'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 8px', color: '#ffffff', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>${(reseller.balance || 0).toFixed(2)}</span>
                      <button
                        onClick={() => handleEditBalance(reseller)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#3b198e',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Edit balance"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '10px 8px', color: '#ffffff', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{reseller.discount_percentage || 0}%</span>
                      <button
                        onClick={() => handleEditDiscount(reseller)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#3b198e',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <button
                      onClick={() => handleDeactivateApiKey(reseller.id, reseller.email)}
                      style={{
                        backgroundColor: '#f59e0b',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'background-color 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
                      title="Deactivate API key and invalidate all API links"
                    >
                      <Key className="h-4 w-4" />
                      Revoke
                    </button>
                  </td>
                  <td style={{ padding: '10px 8px' }}>{getStatusBadge(reseller.status, reseller.deactivated_by)}</td>
                  <td style={{ padding: '10px 8px', color: '#9ca3af', fontSize: '14px', whiteSpace: 'nowrap' }}>{formatDate(reseller.applied_at)}</td>
                  <td style={{ padding: '10px 8px', color: '#9ca3af', fontSize: '14px', whiteSpace: 'nowrap' }}>{formatDate(reseller.approved_at)}</td>
                  <td style={{ padding: '10px 8px' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      {reseller.status === 'approved' ? (
                        <button
                          onClick={() => handleDeactivate(reseller.id, reseller.email)}
                          style={{
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                        >
                          <Ban className="h-4 w-4" />
                          Deactivate
                        </button>
                      ) : reseller.status === 'deactivated' ? (
                        <>
                          <button
                            onClick={() => handleReactivate(reseller.id, reseller.email)}
                            style={{
                              backgroundColor: '#22c55e',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
                          >
                            <CheckCircle className="h-4 w-4" />
                            Reactivate
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(reseller.id, reseller.email)}
                            style={{
                              backgroundColor: '#7f1d1d',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#991b1b'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7f1d1d'}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Discount Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
              Edit Discount Percentage
            </h3>
            <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '16px' }}>
              {selectedReseller?.discord_username} ({selectedReseller?.email})
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Discount Percentage (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={editDiscount}
                onChange={(e) => setEditDiscount(e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSaveDiscount}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d1270'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b198e'}
              >
                Save
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a5a5a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
              Edit Password
            </h3>
            <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '16px' }}>
              {selectedReseller?.discord_username} ({selectedReseller?.email})
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                New Password
              </label>
              <input
                type="text"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                placeholder="Enter new password"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSavePassword}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d1270'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b198e'}
              >
                Save
              </button>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a5a5a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Balance Modal */}
      {showBalanceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
              Edit Balance
            </h3>
            <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '16px' }}>
              {selectedReseller?.discord_username} ({selectedReseller?.email})
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Balance ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editBalance}
                onChange={(e) => setEditBalance(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSaveBalance}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d1270'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b198e'}
              >
                Save
              </button>
              <button
                onClick={() => setShowBalanceModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a5a5a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resellers;
